import { Button, Dropdown, Input, Select, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import { ChevronsDownUp, ChevronsUpDown, Columns3 } from 'lucide-react';
import { ORG_UNIT_TYPES } from '@pams/shared';
import { FilterField } from '@/components/FilterField';
import { READ_ONLY_TOOLTIP } from '@/lib/accessRole';

/**
 * Search, filters, expand/collapse-all, column visibility, and the primary action.
 *
 * Search is not a client-side filter over the loaded tree: typing switches the screen to
 * FLAT mode, backed by `GET /org-units`, which is the only endpoint that can search and
 * sort. One search box, one behaviour, and no second filtering implementation to get
 * subtly different.
 */
export type OrgUnitToolbarProps = {
  search: string;
  onSearchChange: (value: string) => void;
  type: string | undefined;
  onTypeChange: (value: string | undefined) => void;
  status: 'active' | 'all';
  onStatusChange: (value: 'active' | 'all') => void;
  columnItems: MenuProps['items'];
  onExpandAll: () => void;
  onCollapseAll: () => void;
  /** Hidden in flat mode, where there is nothing to expand. */
  showExpansion: boolean;
  canWrite: boolean;
  onCreate: () => void;
};

export const OrgUnitToolbar = ({
  search,
  onSearchChange,
  type,
  onTypeChange,
  status,
  onStatusChange,
  columnItems,
  onExpandAll,
  onCollapseAll,
  showExpansion,
  canWrite,
  onCreate,
}: OrgUnitToolbarProps) => (
  <>
    <FilterField label="Search" className="w-64">
      <Input.Search
        allowClear
        placeholder="Name or short code"
        value={search}
        onChange={(event) => onSearchChange(event.target.value)}
        data-testid="org-search"
      />
    </FilterField>

    <FilterField label="Type" className="w-44">
      <Select
        allowClear
        className="w-full"
        placeholder="Any"
        value={type}
        onChange={onTypeChange}
        options={ORG_UNIT_TYPES.map((value) => ({ value, label: value.toLowerCase() }))}
        data-testid="org-type-filter"
      />
    </FilterField>

    <FilterField label="Status" className="w-40">
      <Select
        className="w-full"
        value={status}
        onChange={onStatusChange}
        options={[
          { value: 'active', label: 'Active only' },
          { value: 'all', label: 'Active and inactive' },
        ]}
        data-testid="org-status-filter"
      />
    </FilterField>

    <div className="ml-auto flex items-end gap-2">
      {showExpansion && (
        <>
          <Button icon={<ChevronsUpDown className="size-4" aria-hidden />} onClick={onExpandAll}>
            Expand all
          </Button>
          <Button icon={<ChevronsDownUp className="size-4" aria-hidden />} onClick={onCollapseAll}>
            Collapse all
          </Button>
        </>
      )}

      <Dropdown menu={{ items: columnItems }} trigger={['click']} placement="bottomRight">
        <Button icon={<Columns3 className="size-4" aria-hidden />}>Columns</Button>
      </Dropdown>

      <Tooltip title={canWrite ? undefined : READ_ONLY_TOOLTIP}>
        <Button type="primary" disabled={!canWrite} onClick={onCreate}>
          Add unit
        </Button>
      </Tooltip>
    </div>
  </>
);
